import React, { useEffect, useRef, useState } from 'react';
import { Asset } from 'contentful';
import Image from 'next/image';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import axios from 'axios';
import logger from '@greenlightme/logger';
import { captureError, trackRumEvent } from '../../utils/datadogRum';
import { useVideo } from '~/hooks/useVideo';
import { useInView } from 'react-intersection-observer';

interface AppFeatureAnimationItemProps {
  media: Asset;
  play?: boolean;
  onEnded?: () => void;
}

export function AppFeatureAnimationItem({
  media,
  play,
  onEnded
}: AppFeatureAnimationItemProps) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [lottieData, setLottieData] = useState(null);
  const [played, setPlayed] = useState(false);
  const contentType = media.fields.file.contentType;
  const [containerRef] = useInView({
    triggerOnce: true,
    threshold: 0.25
  });

  const isVideo =
    contentType?.includes('video') || contentType?.includes('mp4');
  const { videoRef, sourceUrl } = useVideo(
    isVideo ? media.fields.file.url : undefined,
    {
      manageVisibility: true,
      shouldPlay: !!play,
      intersectionThreshold: 0.25,
      deferUntilVisible: false
    }
  );

  const handleEnded = () => {
    onEnded?.();
    setPlayed(true);
  };

  useEffect(() => {
    if (contentType.includes('json') && play && !lottieData) {
      axios
        .get(media.fields.file.url)
        .then(({ data }) => setLottieData(data))
        .catch((err) => {
          logger.error('Error fetching lottie data', err);
          console.error('Error fetching lottie data', err);
        });
    }
  }, [contentType, play, lottieData]);

  useEffect(() => {
    if (videoRef.current) {
      if (play) {
        if (played) {
          videoRef.current.currentTime = 0;
        }
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((err: DOMException) => {
            if (err.name === 'NotAllowedError') {
              trackRumEvent('autoplay_blocked', {
                page_url: window.location.href,
                component: 'AppFeatureAnimationItem'
              });
            }
            logger.error('Error playing video', err);
            console.error('Error playing video', err);
            captureError(err);
          });
        }
      } else {
        videoRef.current.pause();
      }
    }

    if (lottieRef.current) {
      if (play) {
        if (played) {
          lottieRef.current.goToAndStop(0);
        }
        lottieRef.current.play();
      } else {
        lottieRef.current.stop();
      }
    }
  }, [videoRef.current, lottieRef.current, play, played]);

  if (contentType.includes('image')) {
    const imgWidth = media.fields.file.details.image?.width;
    const imgHeight = media.fields.file.details.image?.height;
    const aspectStyle =
      imgWidth && imgHeight
        ? { aspectRatio: `${imgWidth}/${imgHeight}` }
        : undefined;
    const rawUrl = media.fields.file.url;
    const httpsUrl = rawUrl.startsWith('http') ? rawUrl : `https:${rawUrl}`;
    const lqipUrl = `${httpsUrl}?w=24&q=20&fm=jpg`;
    return (
      <div ref={containerRef} className="relative h-full w-full">
        <div
          style={{
            ...aspectStyle,
            backgroundColor: '#f0f0f0',
            backgroundImage: `url(${lqipUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(12px)',
            transform: 'scale(1.05)'
          }}
          className="h-full w-full"
          data-testid="app-feature-animation-image-placeholder"
        />
        <Image
          src={media.fields.file.url}
          alt={media.fields.title}
          width={imgWidth}
          height={imgHeight}
          className={`absolute inset-0 h-full w-full object-fill opacity-0 transition-opacity duration-500`}
          loader={({ src }) => src}
          unoptimized={true}
          loading="lazy"
          onLoadingComplete={(img: HTMLImageElement) => {
            img.classList.remove('opacity-0');
            img.classList.add('opacity-100');
          }}
          data-testid="app-feature-animation-image"
        />
      </div>
    );
  }

  if (contentType.includes('video')) {
    return (
      <video
        key={media.fields.file.url}
        ref={videoRef}
        muted
        playsInline
        preload="metadata"
        crossOrigin="anonymous"
        disablePictureInPicture
        controls={false}
        className="max-h-full"
        data-testid="app-feature-animation-video"
        onEnded={handleEnded}
        onLoadedData={() => {
          if (play && videoRef.current) {
            videoRef.current.play().catch((err: DOMException) => {
              if (err.name === 'NotAllowedError') {
                trackRumEvent('autoplay_blocked', {
                  page_url: window.location.href,
                  component: 'AppFeatureAnimationItem:onLoadedData'
                });
              }
              logger.error('Error playing video on load', err);
              console.error('Error playing video on load', err);
              captureError(err);
            });
          }
        }}
      >
        <source src={sourceUrl} type={contentType} />
        <track kind="captions" srcLang="en" />
      </video>
    );
  }

  if (contentType.includes('json')) {
    return (
      <Lottie
        lottieRef={lottieRef}
        animationData={lottieData}
        autoPlay={false}
        loop={false}
        onComplete={handleEnded}
        className="h-full w-full object-fill"
        data-testid="app-feature-animation-lottie"
      />
    );
  }

  return null;
}
